import type { ClientAuditAction } from '@jam/shared';
import { authHeaders } from './auth-headers';
import { logger } from './logger';

/**
 * Buffered audit-event poster. Events queue in memory and flush every
 * 5s, on `pagehide`, on `visibilitychange:hidden`, or when the queue
 * hits 20 entries. Fire-and-forget — a transient network blip never
 * blocks the user's primary action; the server-side mutation hooks are
 * the source of truth for tamper-evident events.
 */

export type AuditAction = ClientAuditAction;

interface QueuedEvent {
  action: ClientAuditAction;
  target?: string;
  detail?: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_THRESHOLD = 20;

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installedLifecycle = false;

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  try {
    const headers = await authHeaders();
    const res = await fetch('/api/audit/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ events: batch }),
    });
    if (!res.ok) {
      logger.warn('audit.flush failed', {
        status: res.status,
        count: batch.length,
      });
    }
  } catch (err) {
    // Network gone, signed-out, etc. — drop. Audit is best-effort by
    // design; the server-side hooks are the source of truth for
    // tamper-evident events anyway.
    logger.warn('audit.flush threw', {
      error: (err as Error).message,
      count: batch.length,
    });
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

function installLifecycleHooks(): void {
  if (installedLifecycle) return;
  installedLifecycle = true;
  if (typeof window === 'undefined') return;
  // `pagehide` is more reliable than `beforeunload` (fires on bf-cache
  // navigation too). `visibilitychange` covers tab-switch flushes so a
  // backgrounded tab doesn't sit on unsent events for hours.
  window.addEventListener('pagehide', () => {
    void flush();
  });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}

/**
 * Append-only client-side audit pipe. Buffered + best-effort. Errors
 * never propagate. Pass a `target` (free-form id — entity name, brief
 * id, etc.) and optional `meta` for small string-keyed context.
 */
export async function auditLog(
  action: ClientAuditAction,
  opts: { target?: string; meta?: Record<string, unknown> } = {},
): Promise<void> {
  installLifecycleHooks();
  const event: QueuedEvent = {
    action,
    ...(opts.target !== undefined ? { target: opts.target } : {}),
    ...(opts.meta !== undefined ? { detail: opts.meta } : {}),
  };
  queue.push(event);
  if (queue.length >= FLUSH_THRESHOLD) {
    void flush();
  } else {
    scheduleFlush();
  }
}

/**
 * Force any queued events to flush immediately. Test-only.
 */
export async function _flushAuditQueueForTests(): Promise<void> {
  await flush();
}

// ──────────────────────────────────────────────────────────────────────
// Read APIs — for the user history feed (ambient panel) + the admin
// audit explorer. Polled, not subscribed: the unified `audit/*`
// collection is server-only-write and the new rule denies non-superAdmin
// reads of forensic events, so a Firestore `onSnapshot` would 403 most
// of the time.
// ──────────────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  at: string;
  actorUid: string;
  actorEmail?: string;
  orgId: string | null;
  action: string;
  target?: string;
  detail?: Record<string, unknown>;
  category: string;
  severity: string;
  userVisible: boolean;
  country?: string;
  deviceClass?: string;
}

export async function fetchMyHistory(limit = 20): Promise<AuditEntry[]> {
  const res = await fetch(`/api/audit/me?limit=${limit}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) return [];
  const body = (await res.json()) as { entries: AuditEntry[] };
  return body.entries;
}

export async function fetchPlatformAudit(opts: {
  orgId?: string;
  limit?: number;
} = {}): Promise<AuditEntry[]> {
  const params = new URLSearchParams();
  if (opts.orgId) params.set('orgId', opts.orgId);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const res = await fetch(`/api/platform/audit${qs ? `?${qs}` : ''}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`audit.list_failed:${res.status}`);
  }
  const body = (await res.json()) as { entries: AuditEntry[] };
  return body.entries;
}
